import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { MdArrowBack } from 'react-icons/md';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import CustomTypeahead from '../../components/CustomTypeahead';
import SpinnerOverlay from '../../components/SpinnerOverlay/SpinnerOverlay';
import Stepper from '../../components/Stepper/Stepper';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import PaymentReceiptStep from '../../components/PaymentReceiptStep/PaymentReceiptStep';
import ContractService from '../../services/contract.service';
import DebtService from '../../services/debt.service';
import PaymentService from '../../services/payment.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import { EntityList, PageHead, Button, FormField, Section, Select, controlClass, textareaClass } from '../../components/EntityList';

// When reached from a contract's own page (:id in the route) → detail
// mode (PICK_DUE → FILL_PAYMENT). When reached from the contracts list →
// list mode (PICK_CONTRACT → PICK_DUE → FILL_PAYMENT).

const LIST_STEPS = {
	PICK_CONTRACT: 1,
	PICK_DUE: 2,
	FILL_PAYMENT: 3,
};

const DETAIL_STEPS = {
	PICK_DUE: 1,
	FILL_PAYMENT: 2,
};

const LIST_STEPPER = [
	{ counter: 1, name: 'Alquiler' },
	{ counter: 2, name: 'Cuota' },
	{ counter: 3, name: 'Cobro' },
];

const DETAIL_STEPPER = [
	{ counter: 1, name: 'Cuota' },
	{ counter: 2, name: 'Cobro' },
];

function ContractPaymentEditor() {
	const { t } = useLabels();
	const navigate = useNavigate();
	const { id: contractId } = useParams();
	const [searchParams] = useSearchParams();
	const returnToParam = searchParams.get('returnTo');
	const isDetailMode = !!contractId;
	const STEPS = isDetailMode ? DETAIL_STEPS : LIST_STEPS;
	const STEPPER_STEPS = isDetailMode ? DETAIL_STEPPER : LIST_STEPPER;

	const fallbackPath = isDetailMode ? `/AdminScreen/contracts/contract/${contractId}` : '/AdminScreen/contracts';
	const backTarget = () => {
		if (!returnToParam) return fallbackPath;
		const target = new URL(returnToParam, window.location.origin);
		return `${target.pathname}${target.search}`;
	};

	const [pageLoading, setPageLoading] = useState(isDetailMode);
	const [pageError, setPageError] = useState('');
	const [contractProp, setContractProp] = useState(null);

	const [step, setStep] = useState(STEPS.PICK_DUE);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	// List mode — contracts with pending dues
	const [contracts, setContracts] = useState([]);

	// Dues aggregated across all debts of the selected contract
	const [dues, setDues] = useState([]);
	const [selectedDue, setSelectedDue] = useState(null);

	// Payment fields
	const [amount, setAmount] = useState('');
	const [date, setDate] = useState(null);
	const [currency, setCurrency] = useState('');
	const [folder, setFolder] = useState('');
	const [observations, setObservations] = useState('');
	const [comprobante, setComprobante] = useState('');
	const [unpaidAmount, setUnpaidAmount] = useState(null);
	const [paidBy, setPaidBy] = useState('');
	const [paymentMethod, setPaymentMethod] = useState('');

	// Receipt step
	const [showReceipt, setShowReceipt] = useState(false);
	const [createdPaymentId, setCreatedPaymentId] = useState(null);

	// Load dues for a given contract object
	const loadDuesForContract = async (contract) => {
		setLoading(true);
		try {
			const debtsRes = await ContractService.getDebtsForContract(contract._id);
			const debts = Array.isArray(debtsRes.data) ? debtsRes.data : [];

			const dueArrays = await Promise.all(
				debts.map(async (debt) => {
					const { data } = await DebtService.getDuesForDebt(debt._id);
					const unpaid = (Array.isArray(data) ? data : []).filter((d) => !d.paid);
					return unpaid.map((due) => ({ ...due, _debt: debt }));
				})
			);

			setDues(dueArrays.flat());
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (isDetailMode) {
			let isCurrent = true;
			const fetchContract = async () => {
				setPageLoading(true);
				try {
					const { data } = await ContractService.getDetails(contractId);
					if (!isCurrent) return;
					setContractProp(data);
					setStep(DETAIL_STEPS.PICK_DUE);
					await loadDuesForContract(data);
				} catch (err) {
					if (isCurrent) setPageError(getError(err));
				} finally {
					if (isCurrent) setPageLoading(false);
				}
			};
			fetchContract();
			return () => {
				isCurrent = false;
			};
		}

		setStep(LIST_STEPS.PICK_CONTRACT);
		setContracts([]);
		let isCurrent = true;
		const prefetch = async () => {
			setLoading(true);
			try {
				const { data } = await ContractService.getContractsWithUnpaidDues();
				if (isCurrent) setContracts(Array.isArray(data) ? data : []);
			} catch (err) {
				toast.error(getError(err));
			} finally {
				if (isCurrent) setLoading(false);
			}
		};
		prefetch();
		return () => {
			isCurrent = false;
		};
	}, [isDetailMode, contractId]);

	// Step handlers
	const handleContractSelect = async (selected) => {
		if (!selected || selected.length === 0) return;
		await loadDuesForContract(selected[0]);
	};

	const handleDueSelect = (selected) => {
		if (!selected || selected.length === 0) {
			setSelectedDue(null);
			return;
		}
		const due = selected[0];
		setSelectedDue(due);
		setUnpaidAmount(due.unpaidAmount);
		setAmount(due.unpaidAmount != null ? due.unpaidAmount : '');
		setCurrency(due._debt?.currency || '');
		setFolder(due._debt?.owner?.folder || '');
	};

	const handleAmountChange = (e) => {
		const val = parseFloat(e.target.value);
		if (e.target.value === '') {
			setAmount('');
		} else if (!isNaN(val) && (unpaidAmount == null || val <= unpaidAmount)) {
			setAmount(val);
		}
	};

	const uploadComprobante = async () => {
		if (!comprobante || comprobante.length === 0) {
			toast.warning(t('msg.selectFile'));
			return;
		}
		const file = comprobante[0];
		const formData = new FormData();
		const r = Math.random().toString(36).substring(7);
		formData.append('file', file, (file.name || '').replaceAll(' ', '') + r);
		try {
			const response = await PaymentService.uploadComprobante(formData);
			setComprobante(response.data);
			toast.success(t('msg.fileUploaded'));
		} catch (err) {
			toast.error(getError(err));
		}
	};

	const isFormValid = () => selectedDue && date && amount !== '' && parseFloat(amount) > 0;

	const handleSubmit = async () => {
		if (!isFormValid()) return;
		setSaving(true);
		try {
			const payload = {
				amount: parseFloat(amount),
				date,
				debt: selectedDue._debt._id,
				debtDue: selectedDue._id,
				currency,
				folder,
				observations,
				paidBy,
				paymentMethod,
			};
			if (comprobante && comprobante._id) {
				payload.comprobante = comprobante._id;
			}
			const { data } = await PaymentService.create(payload);
			toast.success(t('msg.paymentCreated'));
			setCreatedPaymentId(data.payment._id);
			setShowReceipt(true);
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setSaving(false);
		}
	};

	const contractLabelKey = (c) => {
		const property = c.property?.name || c.property?.customId || '';
		const owner = c.owner?.name || '';
		const parts = [c.customId, property, owner].filter(Boolean);
		return parts.join(' — ');
	};

	if (pageLoading) {
		return <DetailPageLoader entityLabel='cobro' />;
	}

	if (pageError) {
		return (
			<EntityList>
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{pageError}
				</div>
			</EntityList>
		);
	}

	return (
		<EntityList>
			<Helmet>
				<title>{t('title.addPayment')}</title>
			</Helmet>
			<PageHead
				breadcrumb={[
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: t('entity.contract.plural'), to: '/AdminScreen/contracts' },
					...(isDetailMode
						? [{ label: contractProp?.customId || t('entity.contract'), to: `/AdminScreen/contracts/contract/${contractId}` }]
						: []),
					{ label: showReceipt ? t('title.paymentRegistered') : t('title.addPayment') },
				]}
				title={showReceipt ? t('title.paymentRegistered') : t('title.addPayment')}
			/>

			{showReceipt ? (
				<PaymentReceiptStep paymentId={createdPaymentId} onDone={() => navigate(backTarget())} />
			) : (
				<div className='tw:space-y-4'>
					<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
						<div className='tw:text-[12.5px] tw:text-ink-2'>Registrá un cobro para una cuota pendiente.</div>
						<div className='tw:flex tw:gap-2'>
							<Button icon={<MdArrowBack />} onClick={() => navigate(backTarget())} disabled={saving}>
								Cancelar
							</Button>
							{!isDetailMode && step === LIST_STEPS.PICK_DUE && (
								<Button onClick={() => setStep(LIST_STEPS.PICK_CONTRACT)} disabled={saving}>
									{t('action.goBack')}
								</Button>
							)}
							{step === STEPS.FILL_PAYMENT && (
								<Button onClick={() => setStep(STEPS.PICK_DUE)} disabled={saving}>
									{t('action.goBack')}
								</Button>
							)}
							{!isDetailMode && step === LIST_STEPS.PICK_CONTRACT && (
								<Button variant='primary' disabled={dues.length === 0 || loading} onClick={() => setStep(LIST_STEPS.PICK_DUE)}>
									{t('action.continue')}
								</Button>
							)}
							{step === STEPS.PICK_DUE && (
								<Button variant='primary' disabled={!selectedDue} onClick={() => setStep(STEPS.FILL_PAYMENT)}>
									{t('action.continue')}
								</Button>
							)}
							{step === STEPS.FILL_PAYMENT && (
								<Button variant='primary' onClick={handleSubmit} disabled={!isFormValid() || saving}>
									{saving ? 'Guardando...' : t('action.add')}
								</Button>
							)}
						</div>
					</div>

					<Section>
						<Stepper steps={STEPPER_STEPS} activeStep={step} className='tw:mb-4' />

						<div className='tw:relative'>
							{(loading || saving) && <SpinnerOverlay />}

							{!isDetailMode && step === LIST_STEPS.PICK_CONTRACT && (
								<FormField id='contract-select' label={t('entity.contract')}>
									{contracts.length === 0 && !loading ? (
										<p className='tw:text-ink-2'>{t('msg.noContractsWithPendingDues')}</p>
									) : (
										<CustomTypeahead
											id='contract-select'
											labelKey={contractLabelKey}
											onChange={handleContractSelect}
											options={contracts}
											placeholder={t('form.placeholder.searchContractShort')}
											emptyLabel={t('msg.noContractsFound')}
											linkToCreate=''
											newOption={null}
										/>
									)}
								</FormField>
							)}

							{step === STEPS.PICK_DUE && (
								<FormField id='due-select' label={t('field.pendingDue')}>
									{dues.length === 0 && !loading ? (
										<p className='tw:text-ink-2'>{t('msg.noPendingDuesForContract')}</p>
									) : (
										<CustomTypeahead
											id='due-select'
											labelKey={(o) => o.customId || ''}
											onChange={handleDueSelect}
											options={dues}
											placeholder={t('form.placeholder.searchDue')}
											emptyLabel={t('msg.noPendingDuesFound')}
											linkToCreate=''
											newOption={null}
										/>
									)}
								</FormField>
							)}

							{step === STEPS.FILL_PAYMENT && (
								<div className='tw:space-y-4'>
									<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
										<FormField id='payment-debt' label={t('entity.debt')}>
											<Form.Control id='payment-debt' className={controlClass} value={selectedDue?._debt?.customId || ''} readOnly disabled />
										</FormField>
										<FormField id='payment-due' label={t('entity.due')}>
											<Form.Control id='payment-due' className={controlClass} value={selectedDue?.customId || ''} readOnly disabled />
										</FormField>
									</div>
									<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
										<FormField id='payment-amount' label={t('field.amount')} required>
											<Form.Control
												id='payment-amount'
												type='number'
												className={controlClass}
												value={amount}
												onChange={handleAmountChange}
												min={0}
												max={unpaidAmount != null ? unpaidAmount : undefined}
												disabled={saving}
												onInput={(e) => {
													if (unpaidAmount != null && parseFloat(e.target.value) > unpaidAmount) {
														e.target.value = unpaidAmount;
													}
												}}
											/>
											{unpaidAmount != null && (
												<Form.Text muted>
													{t('msg.maximum')}: {unpaidAmount}
												</Form.Text>
											)}
										</FormField>
										<FormField id='payment-date' label={t('field.date')} required>
											<DatePicker
												selected={date}
												onChange={(d) => setDate(d)}
												dateFormat='dd-MM-yyyy'
												className={controlClass}
												placeholderText='dd-mm-yyyy'
												disabled={saving}
											/>
										</FormField>
									</div>
									<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
										<FormField id='payment-currency' label={t('field.currency')}>
											<Select
												id='payment-currency'
												options={[{ value: 'USD', label: 'USD' }, { value: 'UYU', label: 'UYU' }]}
												value={currency}
												disabled={saving}
												onChange={(e) => setCurrency(e.target.value)}
											/>
										</FormField>
										<FormField id='payment-folder' label={t('field.folder')}>
											<Form.Control id='payment-folder' className={controlClass} value={folder} disabled={saving} onChange={(e) => setFolder(e.target.value)} />
										</FormField>
									</div>
									<FormField id='payment-comprobante' label={t('form.comprobante')}>
										<div className='tw:flex tw:gap-2'>
											<Form.Control
												id='payment-comprobante'
												type='file'
												className={controlClass}
												onChange={(e) => setComprobante(e.target.files)}
												disabled={saving}
											/>
											<Button onClick={uploadComprobante} disabled={saving}>
												{t('action.upload')}
											</Button>
										</div>
									</FormField>
									<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
										<FormField id='payment-paidBy' label={t('form.paidBy')}>
											<Form.Control
												id='payment-paidBy'
												className={controlClass}
												value={paidBy}
												disabled={saving}
												placeholder={t('form.placeholder.payerName')}
												onChange={(e) => setPaidBy(e.target.value)}
											/>
										</FormField>
										<FormField id='payment-method' label={t('form.paymentMedium')}>
											<Form.Control
												id='payment-method'
												className={controlClass}
												value={paymentMethod}
												disabled={saving}
												placeholder={t('form.placeholder.paymentMedium')}
												onChange={(e) => setPaymentMethod(e.target.value)}
											/>
										</FormField>
									</div>
									<FormField id='payment-observations' label={t('field.observations')}>
										<Form.Control
											id='payment-observations'
											as='textarea'
											rows={3}
											className={textareaClass}
											value={observations}
											disabled={saving}
											onChange={(e) => setObservations(e.target.value)}
										/>
									</FormField>
								</div>
							)}
						</div>
					</Section>
				</div>
			)}
		</EntityList>
	);
}

export default ContractPaymentEditor;
